import React, { useState } from "react";
import { SecondaryMarketProvider } from "./SecondaryMarket.context";
import { TabTitle } from "@apps/MarketsApp/components/TabTitle";
import { TabContainer } from "@apps/MarketsApp/components/TabContainer";
import { Banner } from "@appComponents/Banner/Banner";
import VideoPlaceholder from "./icons/video_placeholder.svg?url";
import VideoPlayer from "../../icons/video_player.svg";
import { TabsSection } from "./components/TabsSection/TabsSection";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { FaqSection } from "./components/FaqSection";
import Tooltip from "@appComponents/TooltipComponent";
import CloseIcon from "@assets/svg/close.svg";
import { VideoPlayerModal } from "@apps/MarketsApp/components/VideoPlayerModal";

type SecondaryMarketProps = RouteComponentProps<{ subpage: string }> & {
  userSignedIn: boolean;
  investorId?: number;
};

const SecondaryMarketRoot = (props: SecondaryMarketProps) => {
  const [showPlayer, setShowPlayer] = useState(false);
  const [isVideoBannerOpen, setIsVideoBannerOpen] = useState(true);

  const handleScrollToFaq = () => {
    document
      .getElementById("faq-section")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <TabContainer>
        <TabTitle>Private Equity Secondary Market</TabTitle>
        <div className="flex items-center gap-2 text-xs">
          <Tooltip
            title="Custody of funds"
            showArrow={false}
            tooltipMessage="Client funds are held in the custody of the Virtual Assets Broker situated in the British Virgin Islands. The BF Platform Operator, based in the Cayman Islands, does not provide custody services for clients' funds, nor does it engage in any virtual asset services."
          />
        </div>

        <div className="grid grid-cols-1 gap-4 mt-8">
          <Banner variant="purple" title="Private Equity Custody & Trading">
            Private Equity trades are facilitated by BNK To The Future,
            regulated by CIMA. Shares are held in segregated portfolios under
            Bnk To The Future Capital SPC in the Cayman Islands. Each portfolio
            is legally distinct, ensuring separation of investments.
          </Banner>

          <Banner variant="warning" title="Investing involves risk">
            Investing in early-stage companies involves risk. Where necessary,
            you should consult your legal, tax or other professional advisor. To
            learn more please read our{" "}
            <a
              href={`${process.env.WORDPRESS_DOMAIN}/risk-warnings`}
              target="_blank"
              className="underline font-semibold text-sm"
            >
              Risk Disclosure Statement.
            </a>
          </Banner>

          {isVideoBannerOpen && (
            <div className="rounded-[12px] bg-[#F0EFFA] p-6 mt-4 grid md:grid-cols-[132px_1fr] gap-8 relative">
              <div
                className="absolute top-4 right-4 cursor-pointer"
                onClick={() => setIsVideoBannerOpen(false)}
              >
                <CloseIcon />
              </div>

              <div className="relative">
                <button
                  onClick={() => setShowPlayer(true)}
                  className="bg-transparent p-0 absolute top-[50%] left-[50%] transform -translate-x-1/2 -translate-y-1/2"
                >
                  <VideoPlayer />
                </button>
                <img className="w-full h-full" src={VideoPlaceholder} />
              </div>

              <div className="md:h-full flex flex-col md:justify-between">
                <div>
                  <div className="font-bold">
                    What is the BnkToTheFuture Secondary Market?
                  </div>
                  <div className="mb-4">
                    The BnkToTheFuture Secondary Market gives verified investors
                    the ability to trade private equity shares securely. Buyers
                    and sellers set their own pricing, enabling peer-to-peer
                    trading opportunities beyond the original funding round.
                  </div>
                </div>

                <div className="flex gap-8">
                  <a
                    className="underline font-semibold"
                    onClick={() => setShowPlayer(true)}
                  >
                    Watch video
                  </a>

                  <a
                    className="underline font-semibold"
                    onClick={handleScrollToFaq}
                  >
                    Learn more
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="lg:mt-[80px] md:mt-[40px] mt-[20px]">
          <TabsSection
            match={props.match}
            history={props.history}
            userSignedIn={props.userSignedIn}
          />
        </div>

        <div
          id="faq-section"
          className="border border-grey-lighter rounded-[12px] px-8 py-4 md:mt-[50px] mt-[20px]"
        >
          <FaqSection />
        </div>
      </TabContainer>

      <VideoPlayerModal
        videoId="kad2Y7-Fbh4"
        open={showPlayer}
        onClose={() => setShowPlayer(false)}
      />
    </>
  );
};

const SecondaryMarket = (props: SecondaryMarketProps) => {
  return (
    <SecondaryMarketProvider
      userSignedIn={props.userSignedIn}
      investorId={props.investorId}
    >
      <SecondaryMarketRoot {...props} />
    </SecondaryMarketProvider>
  );
};

export default withRouter(SecondaryMarket);
